'use client'

import React, { useCallback, useEffect, useState } from 'react'

import { appMocks } from '@/api/bridge/app/mocks'
import { deviceMocks } from '@/api/bridge/device/mocks'
import { userMocks } from '@/api/bridge/user/mocks'
import {
  clearAllMockOverrides,
  getMockOverride,
  isMockOverrideEnabled,
  setMockOverride,
  setMockOverrideEnabled
} from '@/lib/bridge/mockStorage'

import styles from './BridgeDataPanel.module.scss'

interface MockFormData {
  // Device.getDeviceInfo
  deviceInfo: {
    osVer: string
    packageName: string
    appVer: string
    sdkVer: string
    appId: string
    deviceModel: string
    deviceBrand: string
    width: number
    height: number
    trackingId: string
  }
  // User data
  uid: string
  gender: string
  birthYear: number
  isTester: boolean
  // App.getConfig
  config: {
    prepareViewHidden: boolean
    offerwallListTitle: string
    navigationHelpButtonType: string
    listType: string
    enablePopupBannerExtension: boolean
  }
  // App.getColor
  color: {
    primaryColor: string
    primaryColorVariant: string
    onPrimaryColor: string
    listItemBackgroundColor: string
    listItemTextColor: string
    listItemTitleTextColor: string
    listItemSubtitleTextColor: string
    listItemRewardTextColor: string
  }
}

export default function BridgeDataPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [isEnabled, setIsEnabled] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    device: false,
    user: false,
    appConfig: false,
    appColor: false
  })
  const [formData, setFormData] = useState<MockFormData>({
    deviceInfo: deviceMocks.getDeviceInfo,
    uid: userMocks.getUid.uid,
    gender: userMocks.getGender.gender,
    birthYear: userMocks.getBirthYear.birthYear,
    isTester: userMocks.isTester.isTester,
    config: appMocks.getConfig,
    color: appMocks.getColor
  })

  // 마운트 시 localStorage에서 데이터 로드
  useEffect(() => {
    const enabled = isMockOverrideEnabled()
    setIsEnabled(enabled)

    // Device.getDeviceInfo 오버라이드 로드
    const deviceOverride = getMockOverride('Device', 'getDeviceInfo')
    if (deviceOverride) {
      setFormData(prev => ({
        ...prev,
        deviceInfo: { ...prev.deviceInfo, ...deviceOverride }
      }))
    }

    // User 오버라이드 로드
    const uidOverride = getMockOverride('User', 'getUid')
    if (uidOverride) {
      setFormData(prev => ({ ...prev, uid: uidOverride.uid }))
    }

    const genderOverride = getMockOverride('User', 'getGender')
    if (genderOverride) {
      setFormData(prev => ({ ...prev, gender: genderOverride.gender }))
    }

    const birthYearOverride = getMockOverride('User', 'getBirthYear')
    if (birthYearOverride) {
      setFormData(prev => ({ ...prev, birthYear: birthYearOverride.birthYear }))
    }

    const isTesterOverride = getMockOverride('User', 'isTester')
    if (isTesterOverride) {
      setFormData(prev => ({ ...prev, isTester: isTesterOverride.isTester }))
    }

    // App.getConfig 오버라이드 로드
    const configOverride = getMockOverride('App', 'getConfig')
    if (configOverride) {
      setFormData(prev => ({
        ...prev,
        config: { ...prev.config, ...configOverride }
      }))
    }

    // App.getColor 오버라이드 로드
    const colorOverride = getMockOverride('App', 'getColor')
    if (colorOverride) {
      setFormData(prev => ({
        ...prev,
        color: { ...prev.color, ...colorOverride }
      }))
    }
  }, [])

  // 그룹 토글
  const toggleGroup = useCallback((group: string) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }))
  }, [])

  // 마스터 토글 변경
  const handleEnabledToggle = useCallback(() => {
    const newEnabled = !isEnabled
    setIsEnabled(newEnabled)
    setMockOverrideEnabled(newEnabled)
  }, [isEnabled])

  // 필드 변경 핸들러
  const handleDeviceInfoChange = useCallback((field: keyof MockFormData['deviceInfo'], value: string | number) => {
    setFormData(prev => ({
      ...prev,
      deviceInfo: { ...prev.deviceInfo, [field]: value }
    }))
  }, [])

  const handleUserChange = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleConfigChange = useCallback((field: keyof MockFormData['config'], value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [field]: value }
    }))
  }, [])

  const handleColorChange = useCallback((field: keyof MockFormData['color'], value: string) => {
    setFormData(prev => ({
      ...prev,
      color: { ...prev.color, [field]: value }
    }))
  }, [])

  // 모든 오버라이드 초기화
  const handleResetAll = useCallback(() => {
    if (!confirm('모든 커스텀 데이터를 초기화하시겠습니까?')) return

    clearAllMockOverrides()
    setMockOverrideEnabled(false)
    window.location.reload()
  }, [])

  // 변경사항 저장 및 리로드
  const handleApplyAndReload = useCallback(() => {
    // Device.getDeviceInfo 저장
    setMockOverride('Device', 'getDeviceInfo', formData.deviceInfo)

    // User 데이터 저장
    setMockOverride('User', 'getUid', { uid: formData.uid })
    setMockOverride('User', 'getGender', { gender: formData.gender })
    setMockOverride('User', 'getBirthYear', { birthYear: formData.birthYear })
    setMockOverride('User', 'isTester', { isTester: formData.isTester })

    // App.getConfig 저장
    setMockOverride('App', 'getConfig', formData.config)

    // App.getColor 저장
    setMockOverride('App', 'getColor', formData.color)

    // 페이지 리로드
    window.location.reload()
  }, [formData])

  const checkedInNative = () => {
    if (typeof window === 'undefined') {
      return 'unknown'
    }

    if (
      (window as any).Android?.receiveMessage ||
      (window as any).SharedWeb ||
      (window as any).webkit?.messageHandlers?.adison ||
      (window as any).webkit?.messageHandlers?.app
    ) {
      //native 환경
      return 'native'
    } else {
      //web 환경
      return 'web'
    }
  }

  // 브라우저(web) 환경에서만 노출
  if (checkedInNative() !== 'web') {
    return null
  }

  return (
    <>
      {/* 플로트 토글 버튼 */}
      <button
        className={`${styles.floatButton} ${isOpen ? styles.open : ''} ${isEnabled ? styles.enabled : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title='Bridge Mock Data Panel'
      >
        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
          <circle cx='12' cy='12' r='3' />
          <path d='M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42' />
        </svg>
      </button>

      {/* Panel */}
      {isOpen && (
        <div className={styles.panel}>
          {/* Header */}
          <div className={styles.header}>
            <h3>Bridge Mock Data</h3>
            <label className={styles.masterToggle}>
              <input type='checkbox' checked={isEnabled} onChange={handleEnabledToggle} />
              <span>{isEnabled ? 'Enabled' : 'Disabled'}</span>
            </label>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {/* Device Group */}
            <div className={styles.group}>
              <div className={styles.groupHeader} onClick={() => toggleGroup('device')}>
                <span className={styles.arrow}>{expandedGroups.device ? '▼' : '▶'}</span>
                <span>Device.getDeviceInfo</span>
              </div>
              {expandedGroups.device && (
                <div className={styles.groupContent}>
                  <div className={styles.field}>
                    <label>osVer</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.osVer}
                      onChange={e => handleDeviceInfoChange('osVer', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>packageName</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.packageName}
                      onChange={e => handleDeviceInfoChange('packageName', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>appVer</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.appVer}
                      onChange={e => handleDeviceInfoChange('appVer', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>sdkVer</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.sdkVer}
                      onChange={e => handleDeviceInfoChange('sdkVer', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>appId</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.appId}
                      onChange={e => handleDeviceInfoChange('appId', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>deviceModel</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.deviceModel}
                      onChange={e => handleDeviceInfoChange('deviceModel', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>deviceBrand</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.deviceBrand}
                      onChange={e => handleDeviceInfoChange('deviceBrand', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>width</label>
                    <input
                      type='number'
                      value={formData.deviceInfo.width}
                      onChange={e => handleDeviceInfoChange('width', Number(e.target.value))}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>height</label>
                    <input
                      type='number'
                      value={formData.deviceInfo.height}
                      onChange={e => handleDeviceInfoChange('height', Number(e.target.value))}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>trackingId</label>
                    <input
                      type='text'
                      value={formData.deviceInfo.trackingId}
                      onChange={e => handleDeviceInfoChange('trackingId', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* User Group */}
            <div className={styles.group}>
              <div className={styles.groupHeader} onClick={() => toggleGroup('user')}>
                <span className={styles.arrow}>{expandedGroups.user ? '▼' : '▶'}</span>
                <span>User</span>
              </div>
              {expandedGroups.user && (
                <div className={styles.groupContent}>
                  <div className={styles.field}>
                    <label>uid (User.getUid)</label>
                    <input type='text' value={formData.uid} onChange={e => handleUserChange('uid', e.target.value)} />
                  </div>
                  <div className={styles.field}>
                    <label>gender (User.getGender)</label>
                    <select value={formData.gender} onChange={e => handleUserChange('gender', e.target.value)}>
                      <option value='man'>man</option>
                      <option value='woman'>woman</option>
                      <option value='other'>other</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>birthYear (User.getBirthYear)</label>
                    <input
                      type='number'
                      value={formData.birthYear}
                      onChange={e => handleUserChange('birthYear', Number(e.target.value))}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>isTester (User.isTester)</label>
                    <label className={styles.checkbox}>
                      <input
                        type='checkbox'
                        checked={formData.isTester}
                        onChange={e => handleUserChange('isTester', e.target.checked)}
                      />
                      <span>{formData.isTester ? 'Yes' : 'No'}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* App.getConfig Group */}
            <div className={styles.group}>
              <div className={styles.groupHeader} onClick={() => toggleGroup('appConfig')}>
                <span className={styles.arrow}>{expandedGroups.appConfig ? '▼' : '▶'}</span>
                <span>App.getConfig</span>
              </div>
              {expandedGroups.appConfig && (
                <div className={styles.groupContent}>
                  <div className={styles.field}>
                    <label>prepareViewHidden</label>
                    <label className={styles.checkbox}>
                      <input
                        type='checkbox'
                        checked={formData.config.prepareViewHidden}
                        onChange={e => handleConfigChange('prepareViewHidden', e.target.checked)}
                      />
                      <span>{formData.config.prepareViewHidden ? 'Yes' : 'No'}</span>
                    </label>
                  </div>
                  <div className={styles.field}>
                    <label>offerwallListTitle</label>
                    <input
                      type='text'
                      value={formData.config.offerwallListTitle}
                      onChange={e => handleConfigChange('offerwallListTitle', e.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label>navigationHelpButtonType</label>
                    <select
                      value={formData.config.navigationHelpButtonType}
                      onChange={e => handleConfigChange('navigationHelpButtonType', e.target.value)}
                    >
                      <option value='Icon'>Icon</option>
                      <option value='Text'>Text</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>listType</label>
                    <select
                      value={formData.config.listType}
                      onChange={e => handleConfigChange('listType', e.target.value)}
                    >
                      <option value='Feed'>Feed</option>
                      <option value='List'>List</option>
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>enablePopupBannerExtension</label>
                    <label className={styles.checkbox}>
                      <input
                        type='checkbox'
                        checked={formData.config.enablePopupBannerExtension}
                        onChange={e => handleConfigChange('enablePopupBannerExtension', e.target.checked)}
                      />
                      <span>{formData.config.enablePopupBannerExtension ? 'Yes' : 'No'}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* App.getColor Group */}
            <div className={styles.group}>
              <div className={styles.groupHeader} onClick={() => toggleGroup('appColor')}>
                <span className={styles.arrow}>{expandedGroups.appColor ? '▼' : '▶'}</span>
                <span>App.getColor</span>
              </div>
              {expandedGroups.appColor && (
                <div className={styles.groupContent}>
                  <div className={styles.field}>
                    <label>primaryColor</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.primaryColor}
                        onChange={e => handleColorChange('primaryColor', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.primaryColor}
                        onChange={e => handleColorChange('primaryColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>primaryColorVariant</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.primaryColorVariant}
                        onChange={e => handleColorChange('primaryColorVariant', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.primaryColorVariant}
                        onChange={e => handleColorChange('primaryColorVariant', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>onPrimaryColor</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.onPrimaryColor}
                        onChange={e => handleColorChange('onPrimaryColor', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.onPrimaryColor}
                        onChange={e => handleColorChange('onPrimaryColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>listItemBackgroundColor</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.listItemBackgroundColor}
                        onChange={e => handleColorChange('listItemBackgroundColor', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.listItemBackgroundColor}
                        onChange={e => handleColorChange('listItemBackgroundColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>listItemTextColor</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.listItemTextColor}
                        onChange={e => handleColorChange('listItemTextColor', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.listItemTextColor}
                        onChange={e => handleColorChange('listItemTextColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>listItemTitleTextColor</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.listItemTitleTextColor}
                        onChange={e => handleColorChange('listItemTitleTextColor', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.listItemTitleTextColor}
                        onChange={e => handleColorChange('listItemTitleTextColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>listItemSubtitleTextColor</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.listItemSubtitleTextColor}
                        onChange={e => handleColorChange('listItemSubtitleTextColor', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.listItemSubtitleTextColor}
                        onChange={e => handleColorChange('listItemSubtitleTextColor', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={styles.field}>
                    <label>listItemRewardTextColor</label>
                    <div className={styles.colorInput}>
                      <input
                        type='color'
                        value={formData.color.listItemRewardTextColor}
                        onChange={e => handleColorChange('listItemRewardTextColor', e.target.value)}
                      />
                      <input
                        type='text'
                        value={formData.color.listItemRewardTextColor}
                        onChange={e => handleColorChange('listItemRewardTextColor', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button className={styles.resetButton} onClick={handleResetAll}>
              Reset All
            </button>
            <button className={styles.applyButton} onClick={handleApplyAndReload}>
              Apply & Reload
            </button>
          </div>
        </div>
      )}
    </>
  )
}
